import React, { useState, useEffect } from "react";
import {
  Grid,
  Box,
  Stack,
  Typography,
  TextField,
  Button,
  MenuItem,
  Select,
  Chip,
  OutlinedInput,
  FormControl,
  CircularProgress,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { ApiManager } from "../Services/Networking";
import { X } from "lucide-react";
import Layout from "../Components/Layout";

export default function DepartmentForm() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const isEdit = !!state?.dept;
  const fullList = state?.fullList || [];

  const [formData, setFormData] = useState({
    name: state?.dept?.dept_name || "",
  });
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [employeeList, setEmployeeList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialIds, setInitialIds] = useState([]);

  useEffect(() => {
    if (isEdit && state.dept.employees) {
      const ids = state.dept.employees.map((e) => e.id);
      setSelectedEmployees(ids);
      setInitialIds(ids);
    }
    const fetchEmployees = async () => {
      const res = await ApiManager.getEmployees();
      if (res.status === "success") setEmployeeList(res.data);
    };
    fetchEmployees();
  }, [isEdit, state?.dept]);

  const handleSave = async () => {
    setLoading(true);
    const res = isEdit
      ? await ApiManager.updateDepartment(state.dept.dept_id, {
          name: formData.name,
        })
      : await ApiManager.createDepartment({ name: formData.name });

    if (res.status === "success") {
      const deptId = isEdit ? state.dept.dept_id : res.data.dept_id;
      const toAdd = selectedEmployees.filter((id) => !initialIds.includes(id));
      const toRemove = initialIds.filter(
        (id) => !selectedEmployees.includes(id),
      );

      const addPayload =
        toAdd.length > 0
          ? {
              add: toAdd.map((x) => x),
            }
          : {};
      const removePayload =
        toRemove.length > 0
          ? {
              delete: toRemove.map((x) => x),
            }
          : {};

      await ApiManager.updateDeptEmployees(deptId, {
        ...addPayload,
        ...removePayload,
      });

      const updatedDept = {
        dept_id: deptId,
        dept_name: formData.name,
        employees: selectedEmployees.map((id) => ({
          id,
          name: employeeList.find((e) => e.emp_id === id)?.emp_name || id,
        })),
      };

      const updatedList = isEdit
        ? fullList.map((d) => (d.dept_id === deptId ? updatedDept : d))
        : [...fullList, updatedDept];

      navigate("/department", { state: { updatedList }, replace: true });
    }
    setLoading(false);
  };

  return (
    <Layout name={isEdit ? "EDIT DEPARTMENT" : "ADD DEPARTMENT"}>
      <Box maxWidth="md" sx={{ p: 4, width: "100%" }}>
        <Typography variant="h4" mb={3}>
          {isEdit ? "Edit Department" : "Add Department"}
        </Typography>
        <Stack direction="row" justifyContent="flex-end" spacing={2} mb={4}>
          <Button
            onClick={() =>
              navigate("/department", { state: { updatedList: fullList } })
            }
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={loading}
            startIcon={loading && <CircularProgress size={20} />}
          >
            {isEdit ? "Update" : "Save"}
          </Button>
        </Stack>
        <Grid container spacing={3}>
          <Grid size={12}>
            <Typography mb={1} fontWeight="medium">
              Department Name
            </Typography>
            <TextField
              fullWidth
              size="small"
              value={formData.name}
              onChange={(e) => setFormData({ name: e.target.value })}
            />
          </Grid>
          <Grid size={12}>
            <Typography mb={1} fontWeight="medium">
              Employees
            </Typography>
            <FormControl fullWidth>
              <Select
                multiple
                value={selectedEmployees}
                onChange={(e) => setSelectedEmployees(e.target.value)}
                input={<OutlinedInput size="small" />}
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((id) => (
                      <Chip
                        key={id}
                        label={id}
                        size="small"
                        onDelete={() =>
                          setSelectedEmployees((prev) =>
                            prev.filter((i) => i !== id),
                          )
                        }
                        deleteIcon={<X size={14} />}
                        onMouseDown={(e) => e.stopPropagation()}
                      />
                    ))}
                  </Box>
                )}
              >
                {employeeList.map((emp) => (
                  <MenuItem key={emp.emp_id} value={emp.emp_id}>
                    {emp.emp_name} ({emp.emp_id})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Box>
    </Layout>
  );
}
